import classNames from "classnames";
import { ErrorMessage, Formik } from "formik";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";

import { ForgotPasswordModal } from "./ForgotPasswordModal";
import loginStyle from "./login.scss";
import { LoginDetails } from "components/login/LoginView";
import { EMAIL_MAX_LENGTH } from "domain/globalConstants";
import form from "styles/form.scss";
import layoutStyle from "styles/layout.scss";

import testIds from "testIds.json";

interface Props {
    submitHandler: (values: FormValues) => void;
    ssoConfigurationCheckHandler: (values: FormValues["email"]) => Promise<LoginDetails>;
    login: {
        loginSectionVisible: boolean;
        setLoginSectionVisible: (value: boolean) => void;
    };
    email: {
        userEmail: string;
        setUserEmail: (value: string) => void;
    };
    error: {
        errorMessage: string;
        setErrorMessage: (value: string) => void;
    };
    forgotPasswordLinkHidden: boolean;
    samlLoginSectionHidden: boolean;
}

export interface FormValues {
    email: string;
    password: string;
}

export function hideSamlLoginSection(loginDetails?: LoginDetails): boolean {
    if (typeof loginDetails === "undefined") {
        return true;
    }
    switch (loginDetails.loginMethod) {
        case "EMAIL":
            return true;
        case "COMPANY_ID":
        case "COMPANY_ID_AND_EMAIL":
            return false;
    }
    return typeof loginDetails.loginUrl === "undefined";
}

export function hidePasswordLoginSection(loginDetails?: LoginDetails): boolean {
    if (typeof loginDetails === "undefined") {
        return false;
    }
    switch (loginDetails.loginMethod) {
        case "COMPANY_ID":
            return true;
        case "EMAIL":
        case "COMPANY_ID_AND_EMAIL":
            return false;
    }
    return false;
}

export default function LoginForm(props: Props): JSX.Element {
    const { t } = useTranslation();
    const [passwordVisible, togglePasswordVisibility] = useState<boolean>(false);
    const [samlLoginLink, insertSamlLoginLink] = useState<string>("");
    const [passwordLoginSectionHidden, setPasswordLoginSectionHidden] = useState<boolean>(true);
    const [forgotVisible, setForgotVisible] = React.useState<boolean>(false);

    const handleShowingCorrectLoginSection = (email: string) => {
        if (email !== "") {
            props.ssoConfigurationCheckHandler(email).then(function (loginDetails) {
                if (loginDetails.loginMethod === undefined && loginDetails.loginUrl === undefined) {
                    insertSamlLoginLink("");
                } else {
                    insertSamlLoginLink(loginDetails.loginUrl ? loginDetails.loginUrl : "");
                }
                setPasswordLoginSectionHidden(
                    hidePasswordLoginSection({ loginMethod: loginDetails.loginMethod, loginUrl: loginDetails.loginUrl })
                );
            });
            props.login.setLoginSectionVisible(true);
            props.email.setUserEmail(email);
        }
    };

    const handleInvalid = (e: React.SyntheticEvent) => e.preventDefault();
    React.useEffect(() => {
        if (props.error.errorMessage) {
            handleShowingCorrectLoginSection(props.email.userEmail);
        }
    }, [props.error.errorMessage]);

    const initialFormValues = { email: props.error.errorMessage ? props.email.userEmail : "", password: "" };

    return (
        <>
            <Formik
                initialValues={initialFormValues}
                onSubmit={(values) => props.submitHandler(values)}
                validationSchema={object().shape({
                    email: string()
                        .required(t("LoginForm.emptyEmail"))
                        .email(t("LoginForm.invalidEmail"))
                        .max(EMAIL_MAX_LENGTH),
                    password: string().required(t("LoginForm.emptyPassword")),
                })}
            >
                {(formProps) => {
                    const {
                        values,
                        errors,
                        isSubmitting,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        touched,
                        setFieldError,
                    } = formProps;

                    if (values.email !== props.email.userEmail) {
                        props.error.setErrorMessage("");
                    }

                    return (
                        <form onSubmit={handleSubmit}>
                            <div className={loginStyle.formFields}>
                                <div className={classNames(loginStyle.field)}>
                                    <input
                                        autoFocus
                                        id="email"
                                        data-testid={testIds.login.form.usernameInput.itself}
                                        type="text"
                                        className={classNames(loginStyle.input, {
                                            [loginStyle.inputEmailError]: errors.email,
                                            [layoutStyle.disabledContainer]: props.login.loginSectionVisible,
                                        })}
                                        value={values.email}
                                        onChange={handleChange}
                                        onInvalid={handleInvalid}
                                        max={EMAIL_MAX_LENGTH}
                                        disabled={props.login.loginSectionVisible}
                                        required
                                    />
                                    <label
                                        htmlFor="email"
                                        className={classNames(loginStyle.label, {
                                            [loginStyle.inputError]: errors.email,
                                        })}
                                    >
                                        {t("LoginForm.emailAddress")}
                                    </label>
                                    <div
                                        className={classNames(form.error, form.errorStationary)}
                                        data-testid={testIds.login.form.usernameInput.errorLabel}
                                    >
                                        {errors.email}
                                    </div>
                                </div>
                            </div>
                            {!props.login.loginSectionVisible ? (
                                <div className={classNames(form.buttonContainer, loginStyle.samlButtonContainer)}>
                                    <button
                                        className={loginStyle.submitButton}
                                        onClick={() => {
                                            !errors.email && values.email
                                                ? handleShowingCorrectLoginSection(values.email)
                                                : setFieldError("email", t("LoginForm.emptyEmail"));
                                        }}
                                        data-testid={testIds.login.form.continueButton}
                                    >
                                        {t("Common.continue")}
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div hidden={passwordLoginSectionHidden}>
                                        <div className={loginStyle.formFields}>
                                            <div className={classNames(loginStyle.field)}>
                                                <input
                                                    autoFocus
                                                    id="password"
                                                    data-testid={testIds.login.form.passwordInput.itself}
                                                    type={passwordVisible ? "text" : "password"}
                                                    className={
                                                        props.samlLoginSectionHidden
                                                            ? props.error.errorMessage !== ""
                                                                ? classNames(
                                                                      loginStyle.input,
                                                                      loginStyle.inputEmailError
                                                                  )
                                                                : classNames(loginStyle.input, {
                                                                      [loginStyle.inputEmailError]: errors.password,
                                                                  })
                                                            : loginStyle.input
                                                    }
                                                    value={values.password}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onInvalid={handleInvalid}
                                                    required
                                                />
                                                <label
                                                    htmlFor="password"
                                                    className={
                                                        props.samlLoginSectionHidden
                                                            ? props.error.errorMessage !== ""
                                                                ? classNames(loginStyle.label, loginStyle.inputError)
                                                                : classNames(loginStyle.label, {
                                                                      [loginStyle.inputError]:
                                                                          errors.password && touched.password,
                                                                  })
                                                            : loginStyle.label
                                                    }
                                                >
                                                    {t("LoginForm.password")}
                                                </label>
                                                <button
                                                    onClick={() => togglePasswordVisibility((prevState) => !prevState)}
                                                    className={classNames(
                                                        loginStyle.passwordVisibilityToggle,
                                                        passwordVisible
                                                            ? loginStyle.hidePassword
                                                            : loginStyle.showPassword
                                                    )}
                                                    aria-label={
                                                        passwordVisible ? t("LoginForm.hide") : t("LoginForm.show")
                                                    }
                                                    data-testid={testIds.login.form.passwordInput.toggle}
                                                    type="button"
                                                />
                                                <div
                                                    className={classNames(form.error, form.errorStationary)}
                                                    data-testid={testIds.login.form.passwordInput.errorLabel}
                                                    hidden={!props.samlLoginSectionHidden}
                                                >
                                                    <ErrorMessage name="password" />
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            className={loginStyle.loginError}
                                            data-testid={testIds.login.form.errorLabel}
                                        >
                                            {props.error.errorMessage}
                                        </div>
                                        <div className={classNames(form.buttonContainer, form.buttonContainerVertical)}>
                                            <button
                                                type="submit"
                                                className={loginStyle.submitButton}
                                                disabled={isSubmitting}
                                                data-testid={testIds.login.form.submitButton}
                                            >
                                                {t("Common.loginTitle")}
                                            </button>
                                        </div>
                                    </div>
                                    <div
                                        className={classNames(form.buttonContainer, loginStyle.samlButtonContainer)}
                                        hidden={props.samlLoginSectionHidden}
                                    >
                                        <div className={loginStyle.orSeparator} hidden={passwordLoginSectionHidden}>
                                            <line className={loginStyle.separatorLine}></line>
                                            <label className={loginStyle.samlLabel}>{t("Common.or")}</label>
                                            <line className={loginStyle.separatorLine}></line>
                                        </div>
                                        <button
                                            onClick={() => {
                                                window.location.replace(samlLoginLink);
                                            }}
                                            type="submit"
                                            className={loginStyle.submitButton}
                                            disabled={isSubmitting}
                                            data-testid={testIds.login.form.samlSubmitButton}
                                        >
                                            {t("LoginForm.logInWithCompanyId")}
                                        </button>
                                        <label className={loginStyle.samlLabel}>
                                            <span>{t("LoginForm.companyId")}</span>
                                            {t("LoginForm.companyIdInfo")}
                                        </label>
                                    </div>
                                    <div
                                        className={loginStyle.forgotPasswordLinkContainer}
                                        hidden={props.forgotPasswordLinkHidden}
                                    >
                                        <a
                                            onClick={() => setForgotVisible(true)}
                                            onKeyDown={(e) => e.key === "Enter" && setForgotVisible(true)}
                                            tabIndex={0}
                                            className={loginStyle.forgotPasswordLink}
                                            data-testid={testIds.login.forgotPassword.forgotPasswordButton}
                                        >
                                            {t("Login.forgotPasswordLink")}
                                        </a>
                                    </div>
                                </>
                            )}
                        </form>
                    );
                }}
            </Formik>
            <ForgotPasswordModal
                hide={() => setForgotVisible(false)}
                visible={forgotVisible}
                userEmail={props.email.userEmail && props.email.userEmail}
            />
        </>
    );
}
